import { DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS } from "./constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

export function getRange(value = DEFAULT_TIME_RANGE, siteTimezone) {
  const now = siteTimezone ? dayjs().tz(siteTimezone) : dayjs();
  switch (value) {
    case RANGE_TYPE_LABELS.HOUR_1:
      return [now.subtract(1, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_2:
      return [now.subtract(2, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_12:
      return [now.subtract(12, "hour"), now];
    case RANGE_TYPE_LABELS.HOUR_24:
      return [now.subtract(24, "hour"), now];
    case RANGE_TYPE_LABELS.DAY_1:
      return [now.startOf("day"), now];
    case RANGE_TYPE_LABELS.DAY_MINUS_1: {
      const yesterday = now.subtract(1, "day");
      return [yesterday.startOf("day"), yesterday.endOf("day")];
    }
    case RANGE_TYPE_LABELS.DAY_7:
      return [now.subtract(6, "day").startOf("day"), now];
    case RANGE_TYPE_LABELS.DAY_15:
      return [now.subtract(14, "day").startOf("day"), now];
    case RANGE_TYPE_LABELS.DAY_30:
      return [now.subtract(29, "day").startOf("day"), now];
    case RANGE_TYPE_LABELS.CUSTOM:
    default:
      return [now.subtract(12, "hour"), now];
  }
}
export function applyTheme(theme) {
  document.body.classList.remove("theme-light", "theme-dark");
  document.documentElement.classList.remove("light", "dark");
  document.body.classList.add(`theme-${theme}`);
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.add(theme);
}

export const getDateRangeFormat = range => {
  return Array.isArray(range) && range.length > 0 ? range.map(d => d.format("MMM-DD, YYYY, hh:mm A")).join(" - ") : "";
};

// utils/transformChargerData.js
export function transformChargerData(apiData = [], startTime, endTime) {
  // 0. Sort apiData by generatedAt timestamp (ascending order)
  apiData = [...(apiData || [])].sort((a, b) => {
    const timeA = a?.generatedAt ? dayjs(a.generatedAt).valueOf() : 0;
    const timeB = b?.generatedAt ? dayjs(b.generatedAt).valueOf() : 0;
    return timeA - timeB;
  });

  // 1. Filter schedules >= generatedAt and normalize setpointPower
  apiData = apiData?.map(slots => ({
    ...slots,
    schedule: slots?.schedule?.filter(schedule => schedule?.timestamp >= slots?.generatedAt),
  }));

  const finalData = [];

  // 2. Build continuous data from overlapping schedules
  for (let i = 0; i < apiData?.length - 1; i++) {
    const currentSchedules = apiData[i]?.schedule;
    const nextSchedules = apiData[i + 1]?.schedule;

    for (let j = 0; j < currentSchedules?.length; j++) {
      if (currentSchedules[j]?.timestamp >= nextSchedules?.[0]?.timestamp) {
        break;
      }

      finalData.push([
        new Date(currentSchedules[j]?.timestamp).getTime(),
        currentSchedules[j]?.value?.setpointPower ?? 0,
        {
          ...apiData[i]?.calculationMetrics,
          type: apiData[i]?.scheduleOptimisationType,
        },
      ]);
    }
  }

  // 3. Append last schedule fully
  const lastSchedule = apiData[apiData?.length - 1]?.schedule;
  for (let i = 0; i < lastSchedule?.length; i++) {
    finalData.push([
      new Date(lastSchedule[i]?.timestamp).getTime(),
      lastSchedule[i]?.value?.setpointPower ?? 0,
      {
        ...apiData[apiData.length - 1]?.calculationMetrics,
        type: apiData[apiData.length - 1]?.scheduleOptimisationType,
      },
    ]);
  }

  if (!finalData.length) {
    return { setpointPower: [] };
  }

  const fiveMinutesEpoch = 300_000;
  const fifteenMinutesEpoch = 900_000;

  const finalInjectedData = [];

  function getDefaultZeroOptimisation(baseTimestamp, templateMeta = {}) {
    // When we inject a "zero" point, we also want all calculationMetrics fields to be 0.
    // Keep non-metric identifiers like `type` intact.
    const zeroedMeta = {};
    for (const [key, value] of Object.entries(templateMeta || {})) {
      if (key === "type") {
        zeroedMeta[key] = value;
      } else if (typeof value === "number") {
        zeroedMeta[key] = 0;
      } else if (typeof value === "boolean") {
        zeroedMeta[key] = false;
      } else if (value == null) {
        // treat missing metric values as 0 for injected points
        zeroedMeta[key] = 0;
      } else {
        // fallback: preserve as-is (in case a non-metric field exists)
        zeroedMeta[key] = value;
      }
    }

    return [baseTimestamp, 0, zeroedMeta];
  }

  // 4. Use Set to track existing timestamps (performance fix)
  const existingTimestamps = new Set(finalData.map(item => item[0]));

  // 5. Inject missing zero points when gap > threshold
  for (let i = 0; i < finalData.length; i++) {
    const current = finalData[i];
    const currentTs = current[0];
    const currentMeta = current[2] ?? {};
    const nextTs = finalData[i + 1]?.[0] ?? null;

    // always push original point
    finalInjectedData.push(current);

    const isSmart = currentMeta?.type === "HemsOptimisation";
    const threshold = isSmart ? fifteenMinutesEpoch : fiveMinutesEpoch;
    const candidateTs = currentTs + threshold;

    const shouldInject = nextTs === null ? true : nextTs > candidateTs;
    const alreadyExists = existingTimestamps.has(candidateTs);

    if (shouldInject && !alreadyExists) {
      finalInjectedData.push(getDefaultZeroOptimisation(candidateTs, currentMeta));
      existingTimestamps.add(candidateTs);
    }
  }

  const smartChargingSetpointPower = finalInjectedData
    .filter(item => item[2]?.type === "HemsOptimisation")
    .filter(item => {
      if (!endTime) return true;
      const endTimestampUTC = endTime.valueOf();
      return item[0] <= endTimestampUTC;
    });
  const greenChargingSetpointPower = finalInjectedData.filter(item => item[2]?.type === "GreenCharging");

  return {
    smartChargingSetpointPower,
    greenChargingSetpointPower,
  };
}

export function getEnergyConsumptionDateFormat(date, siteTimezone) {
  const d = siteTimezone ? dayjs(date).tz(siteTimezone) : dayjs(date).utc();
  return d.format("YYYYMMDD");
}

export function transformEnergyConsumptionData(data, startDate, endDate, startTime, endTime, siteTimezone) {
  if (!Array.isArray(data) || !startTime || !endTime) return [];

  const FIVE_MIN_MS = 5 * 60 * 1000;

  // Compute midnight in the site timezone so energy-consumption slots align to local day
  const baseDayLocal = siteTimezone
    ? dayjs(startTime).tz(siteTimezone).startOf("day")
    : dayjs(startTime).utc().startOf("day");
  const baseTs = baseDayLocal.valueOf();

  const startEpoch = dayjs(startTime).valueOf();
  const endEpoch = dayjs(endTime).valueOf();

  // Build timestamp for every data point starting from midnight in site timezone
  const built = data.map((value, index) => {
    const ts = baseTs + index * FIVE_MIN_MS;
    return [ts, value];
  });

  // For same-day queries, cap at "now" in site timezone so we don't show future empty slots
  let cutoffEpoch = endEpoch;
  if (startDate === endDate) {
    const nowLocal = siteTimezone ? dayjs().tz(siteTimezone) : dayjs.utc();
    cutoffEpoch = Math.min(endEpoch, nowLocal.valueOf());
  }

  const finalData = built.filter(([ts]) => {
    return ts >= startEpoch && ts <= cutoffEpoch;
  });
  return finalData;
}

export const getMaxTime = date => {
  const now = dayjs();
  if (date && dayjs(date).isSame(now, "day")) {
    return now;
  }
  return dayjs().endOf("day");
};

export function formatDate(value, siteTimezone) {
  if (value === null || value === undefined || value === "") return "-";
  // Pre-clean common non-ISO suffixes like [UTC] so Date can parse the string
  let cleaned = value;
  try {
    if (typeof cleaned === "string") {
      // remove trailing bracketed zone like [UTC]
      cleaned = cleaned.replace(/\[.*\]$/, "").trim();
      // if still contains a space-separated timezone token at end (e.g. "...Z UTC"), drop it
      const parts = cleaned.split(" ");
      if (parts.length > 1 && /[A-Za-z]{2,}/.test(parts[parts.length - 1])) {
        parts.pop();
        cleaned = parts.join(" ");
      }
    }
  } catch (e) {
    // fall through to parse attempt
  }

  // When siteTimezone is provided, use dayjs.tz for timezone-aware formatting
  if (siteTimezone) {
    const d = dayjs(cleaned);
    if (!d.isValid()) return String(value);
    const tzLabel = getTimezoneLabel(siteTimezone);
    return d.tz(siteTimezone).format("DD/MM/YYYY HH:mm:ss") + ` ${tzLabel}`;
  }

  // Fallback: browser local timezone
  const d = new Date(cleaned);
  if (isNaN(d.getTime())) return String(value);
  const pad = n => String(n).padStart(2, "0");
  const DD = pad(d.getDate());
  const MM = pad(d.getMonth() + 1);
  const YYYY = d.getFullYear();
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const offHours = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const offMins = pad(Math.abs(offsetMinutes) % 60);
  const tzStr = `GMT${sign}${offHours}:${offMins}`;
  return `${DD}/${MM}/${YYYY} ${hh}:${mm}:${ss} ${tzStr}`;
}

export function getRelativeTime(value, siteTimezone) {
  if (!value) return "-";
  const now = Date.now();
  let cleaned = value;
  if (typeof cleaned === "string") {
    cleaned = cleaned.replace(/\[.*\]$/, "").trim();
  }
  const then = new Date(cleaned).getTime();
  if (isNaN(then)) return "-";
  const diffMs = now - then;
  if (diffMs < 0) return "just now";
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value, siteTimezone);
}

export const getTimezoneLabel = (timeZone = "UTC") => {
  if (!timeZone || timeZone === "UTC") return "UTC";

  try {
    const date = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(date);

    const raw = parts.find(p => p.type === "timeZoneName")?.value || "GMT";

    // Intl returns e.g. "GMT+1", "GMT+5:30", "GMT-8" — normalize to UTC prefix
    let label = raw.replace("GMT", "UTC");

    // "UTC" alone (no offset) means UTC+0
    if (label === "UTC") return "UTC";

    return label;
  } catch {
    return "UTC";
  }
};

// Timezone-aware tick formatter for D3 x-axis (shared by LineBarComposedChart & StepChart)
export const tzTickFormat = (date, siteTimezone) => {
  const d = siteTimezone ? dayjs(date).tz(siteTimezone) : dayjs(date);
  if (d.hour() === 0 && d.minute() === 0) return d.format("MMM D");
  return d.format("HH:mm");
};

// Timezone-aware date+time formatter for chart tooltips
export const formatTooltipDateTime = (ts, siteTimezone) => {
  const d = siteTimezone ? dayjs(ts).tz(siteTimezone) : dayjs(ts);
  return d.format("D MMM YYYY HH:mm:ss");
};

// Convert wall-clock dayjs dates to UTC based on siteTimezone.
// For tz-aware dayjs objects (from getRange with siteTimezone), .utc() is sufficient.
// For plain dayjs objects (from AirDatepicker native Date), interpret wall-clock in siteTimezone.
export const toUtcRange = (dateRange, siteTimezone) => {
  if (!siteTimezone || siteTimezone === "UTC") return dateRange;
  return dateRange.map(d => {
    // If the dayjs object is already tz-aware, just convert to UTC
    if (d.$x?.$timezone) return d.utc();
    // Otherwise interpret the wall-clock value in the site timezone
    return dayjs.tz(d.format("YYYY-MM-DDTHH:mm:ss"), siteTimezone).utc();
  });
};

export function calculateOnlineOfflinePercent(data, dateRange) {
  let percentOnline = 0;
  let percentOffline = 0;
  if (data && data.length > 0) {
    let onlineDuration = 0;
    let totalDuration = 0;
    const sortedData = [...data].sort((a, b) => a.x - b.x);
    let startTime = sortedData[0].x;
    let endTime = sortedData[sortedData.length - 1].x;
    if (dateRange?.length === 2) {
      const d1 = toEpoch(dateRange[0]);
      const d2 = toEpoch(dateRange[1]);
      startTime = Math.min(d1, d2);
      endTime = Math.max(d1, d2);
    }
    for (let i = 0; i < sortedData.length - 1; i++) {
      const curr = sortedData[i];
      const next = sortedData[i + 1];
      const segStart = Math.max(curr.x, startTime);
      const segEnd = Math.min(next.x, endTime);
      if (segEnd > segStart) {
        const duration = segEnd - segStart;
        if (curr.y === 1) onlineDuration += duration;
        totalDuration += duration;
      }
    }
    const last = sortedData[sortedData.length - 1];
    if (last.x < endTime) {
      const duration = endTime - last.x;
      if (last.y === 1) onlineDuration += duration;
      totalDuration += duration;
    }
    if (totalDuration > 0) {
      let rawPercent = (onlineDuration / totalDuration) * 100;

      if (rawPercent > 99.9 && rawPercent < 100) {
        percentOnline = 99.9; // Prevent rounding up to 100
      } else if (rawPercent > 0 && rawPercent < 0.1) {
        percentOnline = 0.1; // Prevent rounding down to 0
      } else {
        // 3. Apply standard rounding for all other cases
        percentOnline = Number(rawPercent.toFixed(1));
      }
      percentOffline = 100 - percentOnline;
    }
  }
  return { percentOnline, percentOffline };
}

export const toEpoch = val => {
  if (typeof val === "number") return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.getTime();
};

export function getTimezoneOffset(timeZone, date = new Date()) {
  try {
    // We use 'longOffset' to get the format "GMT+05:30"
    const options = { timeZone, timeZoneName: "longOffset" };

    // formatToParts gives us specific segments of the date
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);

    // Find the part that contains the timezone info
    const timeZoneName = parts.find(part => part.type === "timeZoneName").value;

    // Remove "GMT" prefix to get just "+05:30"
    return timeZoneName;
  } catch {
    return "";
  }
}

export const SIDEBAR_HIDDEN_ROUTES = ["/"];

export const shouldHideSidebar = (pathname = "") => {
  if (!pathname) return false;
  const normalized = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return SIDEBAR_HIDDEN_ROUTES.includes(normalized);
};

export const getCopyrightText = (startYear = 2008) => {
  const currentYear = new Date().getFullYear();
  return `© ${startYear}-${currentYear} Enphase Energy Inc. All rights reserved.`;
};

export function transformUserSetSchedulesData(apiData = []) {
  const setpointPowerData = [];
  for (let i = 0; i < apiData?.length; i++) {
    const currentSchedules = apiData[i]?.schedule;
    const itemType = apiData[i]?.scheduleOptimisationType;

    for (let j = 0; j < currentSchedules?.length; j++) {
      setpointPowerData.push([
        new Date(currentSchedules[j]?.timestamp).getTime(),
        currentSchedules[j]?.value?.setpointPower ?? 0,
        {
          ...apiData[i]?.calculationMetrics,
          type: itemType,
        },
      ]);
    }
  }

  return setpointPowerData;
}

export function transformPowerOfferingData(apiData = []) {
  const list = apiData?.reportsList || apiData || [];
  const powerData = [];
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    const ts = item?.reportedTimestamp ? item.reportedTimestamp * 1000 : null;
    powerData.push([
      ts ? new Date(ts).getTime() : null,
      item?.powerOffered ?? 0,
      {
        ...item?.powerOffered,
        type: "PowerOffering",
      },
    ]);
  }
  return powerData;
}

/**
 * Creates an afterSetExtremes handler for synchronized chart zoom.
 * @param {Function} onZoomChange - callback to report zoom changes
 * @param {string} source - identifier for the chart ("line" or "step")
 * @returns {Function} handler for xAxis.events.afterSetExtremes
 */
export function createZoomHandler(onZoomChange, source) {
  return function (e) {
    if (onZoomChange && !this.chart._isSyncing) {
      if (e.trigger === "zoom" || e.trigger === "pan") {
        onZoomChange({ min: e.min, max: e.max, source });
      } else if (!e.trigger || e.trigger === "resetZoom") {
        onZoomChange(null);
      }
    }
  };
}

export function syncZoomExtremes(chart, zoomExtremes, ownSource) {
  if (!chart || !chart.xAxis?.[0]) return;
  if (zoomExtremes?.source === ownSource) return;
  chart._isSyncing = true;
  if (zoomExtremes) {
    chart.xAxis[0].setExtremes(zoomExtremes.min, zoomExtremes.max, true, false);
  } else {
    chart.xAxis[0].setExtremes(undefined, undefined, true, false);
  }
  chart._isSyncing = false;
}

export { isEnphaseUser } from "./featureAccess";

export const getUserInitials = (user, fallback = "EN") => {
  const parts = `${user?.first_name || ""} ${user?.last_name || ""}`.trim().split(" ").filter(Boolean);
  return parts.length
    ? parts
        .slice(0, 2)
        .map(n => n[0])
        .join("")
        .slice(0, 2)
    : fallback;
};

/**
 * Find the index of the most recent data point with x <= targetX.
 * Expects sorted ascending data where each element is [timestamp, value, ...].
 * @param {Array} data - Array of [timestamp, value, ...] tuples
 * @param {number} targetX - The timestamp to search against
 * @returns {number} Index of the left-closest point, or -1 if none found
 */
export const findLeftIndex = (data, targetX) => {
  if (!data || !data.length) return -1;
  let leftIdx = -1;
  for (let i = 0; i < data.length; i++) {
    if (data[i][0] <= targetX) leftIdx = i;
    else break;
  }
  return leftIdx;
};

/**
 * Find the closest data point to the left of (or at) targetX.
 * Expects data where each element is [timestamp, value, ...].
 * @param {Array} data - Array of [timestamp, value, ...] tuples
 * @param {number} targetX - The timestamp to search against
 * @returns {Array|null} The closest data point tuple, or null
 */
export const findClosestPoint = (data, targetX) => {
  if (!data || !data.length) return null;
  let closest = null;
  let minDiff = Infinity;
  for (let i = 0; i < data.length; i++) {
    const px = data[i][0];
    if (px == null) continue;
    if (px <= targetX) {
      const diff = targetX - px;
      if (diff < minDiff) {
        minDiff = diff;
        closest = data[i];
      }
    }
  }
  return closest;
};

/**
 * Export an SVG element as a PNG image download.
 * Renders the SVG to a canvas at 2x resolution for crisp output.
 * @param {SVGElement} svgElement - The SVG DOM element to export
 * @param {string} filename - Download filename (default: "chart.png")
 */
/**
 * Round a number to a given number of decimal places.
 * @param {number} value - The number to round
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number} Rounded number
 */
export const roundToDecimal = (value, decimals = 2) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/**
 * Format a total number of seconds into a human-readable duration string.
 * @param {number} totalSeconds - Total seconds
 * @returns {string} Formatted string like "2d 3h 15m" or "3h 15m" or "45m" or "30s"
 */
export const formatSecondsToDuration = (totalSeconds = 0) => {
  if (totalSeconds < 60) return `${Math.round(totalSeconds)}s`;
  const totalMins = Math.round(totalSeconds / 60);
  const days = Math.floor(totalMins / 1440);
  const hours = Math.floor((totalMins % 1440) / 60);
  const mins = totalMins % 60;
  if (days > 0) return mins > 0 ? `${days}d ${hours}h ${mins}m` : `${days}d ${hours}h`;
  return hours > 0 ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`) : `${mins}m`;
};

export const exportSVGtoPNG = (svgElement, filename = "chart.png") => {
  const svgData = new XMLSerializer().serializeToString(svgElement);
  const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = svgElement.clientWidth * 2;
    canvas.height = svgElement.clientHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);

    canvas.toBlob(blob => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    }, "image/png");
  };
  img.src = url;
};
